import React, { useEffect, useState } from "react";
import axios from "axios";
import CourseBanner from "../components/CourseBanner";
import { BOOK_STATUS, AMAZON_BOOK_URL, BOOK_PRICE_LABEL } from "../config/bookStatus";

/**
 * BOOK SALES PAGE — /book
 *
 * Public-facing product page for "Stop saying 'I Have To.' Start living 'I GET TO.'"
 * Drives traffic to Lulu Bookstore + sponsorship tier for women's shelters.
 *
 * ╔══════════════════════════════════════════════════════════════════════╗
 * ║  🚦 GO-LIVE SWITCH                                                  ║
 * ║  Currently: PRE_LAUNCH (Lulu reviewing files — no public Buy URL)    ║
 * ║                                                                      ║
 * ║  WHEN LULU APPROVES THE BOOK:                                        ║
 * ║   1. Set BOOK_STATUS = "LIVE" below                                  ║
 * ║   2. Paste real Lulu URL into LULU_BOOKSTORE_URL                     ║
 * ║   3. Redeploy                                                        ║
 * ║  → All "Notify Me" buttons instantly become "Order paperback".       ║
 * ╚══════════════════════════════════════════════════════════════════════╝
 */

const PRE_LAUNCH_GROUP_ID = "book_pre_launch"; // MailerLite group key for these signups
const SAMPLE_GROUP_ID = "191733068420089330"; // MailerLite "Free Book Sample + Calendar" group
const SPONSOR_EMAIL = "mindbodyspirithygiene@gmail.com";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  ocean: "#25a1c2",
  gold: "#7BC8CB", // OCEAN Light Teal — gold #d4af6a retired 2026-06-28 per BRAND_VOICE_MASTER.md
  cream: "#efeae7",
  mint: "#5EEAD4",
};

// ============================================================================
// BuyOrNotify — adaptive CTA. Shows a "Notify Me" email capture when the book
// is in PRE_LAUNCH; shows a real Buy button once BOOK_STATUS === "LIVE".
// ============================================================================
const BuyOrNotify = ({ variant = "hero" }) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | success | error
  const [errorMsg, setErrorMsg] = useState("");

  // LIVE MODE: Show real Buy button + secondary workbook CTA
  if (BOOK_STATUS === "LIVE") {
    const isFooter = variant === "footer";
    return (
      <div className={isFooter ? "flex flex-col items-center gap-3" : "flex flex-col items-start gap-3"}>
        <a
          href={AMAZON_BOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02] hover:shadow-2xl"
          style={{
            background: BRAND.mint,
            color: BRAND.deep,
            fontSize: isFooter ? "16px" : "15px",
            padding: isFooter ? "20px 40px" : "16px 32px",
          }}
          data-testid={isFooter ? "footer-buy-cta" : "buy-now-cta"}
        >
          {isFooter ? `Order your copy · ${BOOK_PRICE_LABEL}` : `Order paperback · ${BOOK_PRICE_LABEL}`}
          <span style={{ fontSize: "18px" }}>→</span>
        </a>
        <button
          type="button"
          onClick={() => {
            const el = document.getElementById("workbook-capture");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            if (typeof window !== "undefined" && typeof window.gtag === "function") {
              window.gtag("event", "free_workbook_cta_click", { source_page: "book", variant });
            }
          }}
          className="inline-flex items-center justify-center gap-3 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5"
          style={{
            background: "transparent",
            color: BRAND.mint,
            border: `2px solid ${BRAND.mint}`,
            fontSize: isFooter ? "16px" : "15px",
            padding: isFooter ? "20px 40px" : "16px 32px",
          }}
          data-testid={isFooter ? "footer-free-workbook-cta" : "hero-free-workbook-cta"}
        >
          Get the FREE I GET TO workbook
          <span style={{ fontSize: "18px" }}>→</span>
        </button>
      </div>
    );
  }

  // PRE_LAUNCH MODE: Email capture
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    setErrorMsg("");

    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        email: email.trim().toLowerCase(),
        firstName: firstName.trim() || "Friend",
        group_id: PRE_LAUNCH_GROUP_ID,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail ||
          "Something hiccupped. Please try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "success") {
    return (
      <div
        className="rounded-2xl p-6 max-w-md"
        style={{
          background: `${BRAND.mint}1a`,
          border: `1px solid ${BRAND.mint}`,
        }}
        data-testid={`notify-success-${variant}`}
      >
        <p
          className="font-serif italic text-lg mb-1"
          style={{ color: variant === "footer" ? BRAND.cream : BRAND.cream }}
        >
          You're on the list. ✦
        </p>
        <p
          className="font-sans text-sm"
          style={{
            color: variant === "footer" ? `${BRAND.cream}cc` : `${BRAND.cream}cc`,
          }}
        >
          The moment paperbacks open for orders, you'll be the first to know.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col sm:flex-row gap-3 w-full max-w-md"
      data-testid={`notify-form-${variant}`}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your email"
        aria-label="Email address"
        className="flex-1 px-5 py-3 rounded-full font-sans text-sm outline-none transition-all focus:ring-2"
        style={{
          background: variant === "footer" ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.95)",
          color: variant === "footer" ? BRAND.cream : BRAND.deep,
          border: `1px solid ${variant === "footer" ? `${BRAND.mint}44` : "transparent"}`,
        }}
        data-testid={`notify-email-${variant}`}
      />
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02] hover:shadow-2xl disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
        style={{
          background: BRAND.mint,
          color: BRAND.deep,
          fontSize: "14px",
        }}
        data-testid={`notify-submit-${variant}`}
      >
        {submitting ? "Sending…" : "Notify me when it's live"}
        <span style={{ fontSize: "16px" }}>→</span>
      </button>
      {status === "error" && (
        <p
          className="font-sans text-xs basis-full"
          style={{ color: variant === "footer" ? "#fda4af" : "#fda4af" }}
          data-testid={`notify-error-${variant}`}
        >
          {errorMsg}
        </p>
      )}
    </form>
  );
};

// ============================================================================
// SampleDownloadForm — captures email on /book, subscribes to MailerLite
// group `free_chapter_sample`, then redirects to /sample-success where the
// 8-page PDF is delivered.
// ============================================================================
const SampleDownloadForm = () => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    setErrorMsg("");

    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        email: email.trim().toLowerCase(),
        firstName: firstName.trim() || "Friend",
        group_id: SAMPLE_GROUP_ID,
      });
      // GA4 conversion event — primary KPI: email captures
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "optin_submit", {
          method: "book_sample_bundle",
          source_page: "book",
          value: 0,
          currency: "USD",
        });
        window.gtag("event", "generate_lead", { currency: "USD", value: 0 });
      }
      // Meta Pixel — Lead conversion (safe no-op until pixel base loads)
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "Free Book Sample", content_category: "lead-magnet", value: 0, currency: "USD" });
      }
      // Mark this device as a returning subscriber so homepage swaps to the "welcome back" hero
      try { localStorage.setItem("mbsh_subscribed", "true"); } catch (_) { /* ignore */ 
      }
      // Pinterest Tag lead conversion
      if (typeof window !== "undefined" && typeof window.pintrk === "function") {
        window.pintrk("track", "lead", { lead_type: "book_sample_bundle" });
      }
      // Hand off to the dedicated success/download page
      window.location.href = "/sample-success";
    } catch (err) {
      // 400 from MailerLite usually means already subscribed — still let her in
      const status = err?.response?.status;
      if (status === 400 || status === 409) {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("event", "optin_submit_existing", { method: "book_sample_bundle" });
        }
        window.location.href = "/sample-success";
        return;
      }
      setErrorMsg(
        err?.response?.data?.detail ||
          "Something hiccupped. Please try again in a moment."
      );
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
      data-testid="sample-form"
    >
      <input
        type="text"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        placeholder="First name (optional)"
        aria-label="First name"
        className="sm:w-48 px-5 py-3 rounded-full font-sans text-sm outline-none transition-all focus:ring-2"
        style={{
          background: "rgba(255,255,255,0.95)",
          color: BRAND.deep,
          border: "1px solid transparent",
        }}
        data-testid="sample-first-name"
      />
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your email"
        aria-label="Email address"
        className="flex-1 px-5 py-3 rounded-full font-sans text-sm outline-none transition-all focus:ring-2"
        style={{
          background: "rgba(255,255,255,0.95)",
          color: BRAND.deep,
          border: "1px solid transparent",
        }}
        data-testid="sample-email"
      />
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02] hover:shadow-2xl disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
        style={{ background: BRAND.mint, color: BRAND.deep, fontSize: "14px" }}
        data-testid="sample-submit"
      >
        {submitting ? "Sending…" : "Send me the 8-page sample"}
        <span style={{ fontSize: "16px" }}>↓</span>
      </button>
      {errorMsg && (
        <p
          className="font-sans text-xs basis-full text-center"
          style={{ color: "#fda4af" }}
          data-testid="sample-error"
        >
          {errorMsg}
        </p>
      )}
    </form>
  );
};

// ============================================================================
// ChooseEditionCta — hero/footer button that scrolls to the two-edition chooser.
// (A buyer picks their edition in the cards; nothing is bought from here.)
// ============================================================================
const scrollToEditions = () => {
  if (typeof document === "undefined") return;
  const el = document.getElementById("editions");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ChooseEditionCta = ({ block = false }) => (
  <div className={block ? "flex flex-col items-center gap-2" : "flex flex-col items-start gap-2"}>
    <span
      className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold"
      style={{ color: BRAND.gold }}
      data-testid="book-pdf-badge"
    >
      Available right now.
    </span>
    <button
      onClick={scrollToEditions}
      className="inline-flex items-center gap-3 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5 hover:shadow-2xl"
      style={{ background: "#F4C36A", color: "#1B6B6E", fontSize: "16px", padding: "18px 34px" }}
      data-testid="book-pdf-primary-cta"
    >
      Choose your edition — instant PDF · $9.99
      <span style={{ fontSize: "18px" }}>→</span>
    </button>
    <span className="font-sans text-xs" style={{ color: `${BRAND.cream}aa` }} data-testid="book-pdf-subcopy">
      PDF Edition — designed to write in
    </span>
  </div>
);

// ============================================================================
// EditionChooser — the two-edition decision. Same book, same tools, same $9.99.
// Two equal cards, neither featured. The big label + the italic line do all the
// distinguishing (covers are identical by design).
// ============================================================================
const EDITIONS = [
  {
    key: "women",
    product: null, // default checkout = the original "for women" edition
    label: "FOR WOMEN",
    title: "I GET TO — for women",
    line: "Because I am a woman, this one speaks to the woman.",
  },
  {
    key: "anyone",
    product: "i_get_to_pdf_anyone",
    label: "FOR ANYONE",
    title: "I GET TO — for anyone",
    line: "Because men asked for it, this one speaks to anyone.",
  },
];

const EditionCard = ({ ed }) => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const buy = async () => {
    if (loading) return;
    setLoading(true);
    setErr("");
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "begin_checkout", {
          currency: "USD",
          value: 9.99,
          items: [{ item_id: ed.product || "i_get_to_pdf_edition", item_name: ed.title, price: 9.99, quantity: 1 }],
        });
      }
      const res = await axios.post(`${API_URL}/api/pdf/checkout`, {
        origin: window.location.origin,
        source: `book-editions-${ed.key}`,
        ...(ed.product ? { product: ed.product } : {}),
      });
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      setErr("Couldn't start checkout — please try again in a moment.");
      setLoading(false);
    }
  };
  return (
    <div
      className="flex flex-col rounded-2xl overflow-hidden border shadow-sm"
      style={{ borderColor: `${BRAND.teal}33`, background: "#ffffff" }}
      data-testid={`edition-card-${ed.key}`}
    >
      {/* BIG LABEL — the whole point. Readable from across the room. */}
      <div className="px-6 py-6 text-center" style={{ background: BRAND.teal }}>
        <span
          className="font-sans font-black uppercase tracking-[0.1em] text-3xl sm:text-4xl leading-none"
          style={{ color: "#ffffff" }}
          data-testid={`edition-label-${ed.key}`}
        >
          {ed.label}
        </span>
      </div>
      <div className="flex flex-col items-center text-center gap-4 px-6 py-8 flex-1">
        <h3 className="font-serif text-xl" style={{ color: BRAND.deep }} data-testid={`edition-title-${ed.key}`}>
          {ed.title}
        </h3>
        <p className="font-serif italic text-base" style={{ color: BRAND.teal }} data-testid={`edition-line-${ed.key}`}>
          {ed.line}
        </p>
        <span className="font-sans font-bold text-2xl" style={{ color: BRAND.deep }} data-testid={`edition-price-${ed.key}`}>
          $9.99
        </span>
        <button
          onClick={buy}
          disabled={loading}
          className="mt-2 inline-flex items-center gap-2 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
          style={{ background: BRAND.deep, color: "#ffffff", fontSize: "15px", padding: "15px 30px" }}
          data-testid={`edition-buy-${ed.key}`}
        >
          {loading ? "Opening secure checkout…" : "Get this one"}
          <span style={{ fontSize: "17px" }}>→</span>
        </button>
        {err && (
          <span className="font-sans text-xs" style={{ color: "#dc2626" }} data-testid={`edition-error-${ed.key}`}>
            {err}
          </span>
        )}
      </div>
    </div>
  );
};

const EditionChooser = () => (
  <section
    id="editions"
    className="px-6 py-16 md:py-24 scroll-mt-24"
    style={{ background: BRAND.cream }}
    data-testid="editions-section"
  >
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-10 md:mb-14">
        <h2 className="font-serif text-3xl md:text-4xl mb-6" style={{ color: BRAND.deep }} data-testid="editions-headline">
          Two editions. One method.
        </h2>
        <div className="max-w-2xl mx-auto flex flex-col gap-4 text-left sm:text-center" style={{ color: BRAND.deep }}>
          <p className="font-sans text-base leading-relaxed">
            When I wrote this, I wrote it for women. That is what you are told to do — pick your niche,
            speak to one person. So I did.
          </p>
          <p className="font-sans text-base leading-relaxed">Then men started telling me it was theirs too.</p>
          <p className="font-sans text-base leading-relaxed">
            So there are two now. <strong>The original</strong>, in the language I first wrote it in for
            women. And a second one with the door opened wider — the same book, the same tools, the same
            practice.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-stretch">
        {EDITIONS.map((ed) => (
          <EditionCard key={ed.key} ed={ed} />
        ))}
      </div>
    </div>
  </section>
);

// ============================================================================
// WorkbookInlineForm — inline free-workbook email capture on /book. Subscribes
// to the workbook MailerLite group and hands off to /workbook-success (which
// auto-downloads the workbook). No standalone page hop from the CTA.
// ============================================================================
const WORKBOOK_GROUP_ID = "192377413184783762"; // MailerLite "Workbook - Homepage-Hero"
const WorkbookInlineForm = () => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        email: email.trim().toLowerCase(),
        firstName: firstName.trim() || "Friend",
        group_id: WORKBOOK_GROUP_ID,
        source: "book-inline-workbook",
      });
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "optin_submit", { method: "book_inline_workbook", source_page: "book", value: 0, currency: "USD" });
        window.gtag("event", "generate_lead", { currency: "USD", value: 0 });
      }
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "Free I GET TO Workbook", content_category: "lead-magnet", value: 0, currency: "USD" });
      }
      try { localStorage.setItem("mbsh_subscribed", "true"); } catch (_) { /* ignore */ }
      if (typeof window !== "undefined" && typeof window.pintrk === "function") {
        window.pintrk("track", "lead", { lead_type: "book_inline_workbook" });
      }
      window.location.href = "/workbook-success";
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400 || status === 409) {
        window.location.href = "/workbook-success";
        return;
      }
      setErrorMsg(err?.response?.data?.detail || "Something hiccupped. Please try again in a moment.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto" data-testid="workbook-inline-form">
      <input
        type="text"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        placeholder="First name (optional)"
        aria-label="First name"
        className="sm:w-48 px-5 py-3 rounded-full font-sans text-sm outline-none"
        style={{ background: "rgba(255,255,255,0.95)", color: BRAND.deep }}
        data-testid="workbook-inline-first-name"
      />
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your email"
        aria-label="Email address"
        className="flex-1 px-5 py-3 rounded-full font-sans text-sm outline-none"
        style={{ background: "rgba(255,255,255,0.95)", color: BRAND.deep }}
        data-testid="workbook-inline-email"
      />
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02] disabled:opacity-60 whitespace-nowrap"
        style={{ background: BRAND.mint, color: BRAND.deep, fontSize: "14px" }}
        data-testid="workbook-inline-submit"
      >
        {submitting ? "Sending…" : "Send me the free workbook"}
        <span style={{ fontSize: "16px" }}>↓</span>
      </button>
      {errorMsg && (
        <p className="font-sans text-xs basis-full text-center" style={{ color: "#fda4af" }} data-testid="workbook-inline-error">
          {errorMsg}
        </p>
      )}
    </form>
  );
};

const BOOK_FAQ = [
  {
    q: "When will my book arrive?",
    a: "Right now — the paperback is available on Amazon and ships in Amazon's standard delivery windows. Prefer it instantly? The full book is also available as a downloadable PDF right on this page — yours the moment you order.",
  },
  {
    q: "Is the book a substitute for therapy or medical care?",
    a: "No. This book is for inspiration, reflection, and a daily practice of tuning in. It is not a substitute for medical, psychological, financial, legal, or therapeutic advice. Please consult qualified professionals for those needs. Your health — mental, physical, spiritual — is yours to steward.",
  },
  {
    q: "Is this a Christian / spiritual / religious book?",
    a: "It's written for the woman tapping into the energies of her day, whoever she is. No identity required. No faith box to check. Take what resonates and leave the rest. The message lives in you, not in me.",
  },
  {
    q: "Why does it cost $19.99?",
    a: "It's the sweet spot. Premium 6x9 trim, 80# white paper, clean interior layout — printed at quality you'll want to live with on your nightstand, priced where a real woman in the messy middle can actually say yes. Royalties keep the lights on and fund what comes next: the journal, the companion guide, the course. Fair to you, sustainable for the work. See my note above.",
  },
  {
    q: "Can I get it on Amazon yet?",
    a: "Yes — it's live on Amazon now. Grab the paperback here: https://www.amazon.com/dp/B0HD8ZKS3J — or get the instant PDF edition right on this page.",
  },
];

const BookPage = () => {
  useEffect(() => {
    // ---- Instagram-safe deep-link to the free-chapter form ----
    // Triggers when path is /free-chapter, /free-sample, or URL has
    // #free-chapter or ?focus=sample. IG strips fragments sometimes, so
    // the path-based aliases are the most reliable.
    const path = window.location.pathname.toLowerCase();
    const hash = window.location.hash.toLowerCase();
    const params = new URLSearchParams(window.location.search);
    const shouldScrollToSample =
      path === "/free-chapter" ||
      path === "/free-sample" ||
      hash === "#free-chapter" ||
      params.get("focus") === "sample";

    if (hash === "#editions") {
      setTimeout(() => {
        const el = document.getElementById("editions");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }

    if (shouldScrollToSample) {
      // Defer to let the page render, then scroll smoothly to the form
      setTimeout(() => {
        const el = document.getElementById("free-chapter");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 250);
    }

    // ---- SEO meta tags ----
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const m = selector.match(/\[([^=]+)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const description =
      "The Mind Body Spirit HYGIENE™ Method by Temple Franklin. Real tools, for real women, in the messy middle — divorce, grief, estrangement, midlife reckoning. Available now in paperback on Amazon and as an instant PDF edition.";
    const url = "https://mindbodyspirithygienetools.com/book";
    const image = "https://mindbodyspirithygienetools.com/book-cover-2026.png";

    // Additive tags only — RouteSeo owns title/description/canonical/og:title/og:image.
    setMeta('meta[name="keywords"]', "content",
      "Temple Franklin, Mind Body Spirit HYGIENE™, I GET TO, midlife awakening, divorce healing, estrangement, grief, women self-help, daily practice, A.C.T. NOW Method, Personal Code of Conduct, evidential medium, HeartMath, Tapped In Publishing"
    );
    setMeta('meta[property="book:author"]', "content", "Temple Franklin");
    setMeta('meta[property="book:isbn"]', "content", "979-8-9964172-1-6");

    // JSON-LD structured data (Book schema → Google rich results)
    const ldId = "book-ld-json";
    let ldScript = document.getElementById(ldId);
    if (!ldScript) {
      ldScript = document.createElement("script");
      ldScript.id = ldId;
      ldScript.type = "application/ld+json";
      document.head.appendChild(ldScript);
    }
    const pdfEdition = (name, sku) => ({
      "@type": "Book",
      name: `Stop Saying "I Have To." Start Living "I GET TO." — ${name}`,
      bookEdition: name,
      bookFormat: "https://schema.org/EBook",
      inLanguage: "en-US",
      author: { "@type": "Person", name: "Temple Franklin" },
      offers: {
        "@type": "Offer",
        sku,
        price: "9.99",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: `${url}#editions`,
      },
    });
    ldScript.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Book",
        name: "Stop Saying \"I Have To.\" Start Living \"I GET TO.\"",
        alternateName: "The Mind Body Spirit HYGIENE™ Method",
        author: { "@type": "Person", name: "Temple Franklin", url: "https://mindbodyspirithygienetools.com/about" },
        publisher: { "@type": "Organization", name: "Tapped In Publishing" },
        inLanguage: "en-US",
        datePublished: "2026-06-11",
        image,
        description,
        url,
        workExample: [
          pdfEdition("PDF edition for women", "i_get_to_pdf_edition"),
          pdfEdition("PDF edition for anyone", "i_get_to_pdf_anyone"),
          {
            "@type": "Book",
            name: "Stop Saying \"I Have To.\" Start Living \"I GET TO.\" — Paperback",
            bookEdition: "Paperback",
            bookFormat: "https://schema.org/Paperback",
            isbn: "979-8-9964172-1-6",
            numberOfPages: 140,
            inLanguage: "en-US",
            author: { "@type": "Person", name: "Temple Franklin" },
            offers: {
              "@type": "Offer",
              price: "19.99",
              priceCurrency: "USD",
              availability: BOOK_STATUS === "LIVE" ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
              url: BOOK_STATUS === "LIVE" ? AMAZON_BOOK_URL : url,
              seller: { "@type": "Organization", name: "Amazon" },
            },
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: BOOK_FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ]);

    // Cleanup the JSON-LD when leaving page so it doesn't bleed into other routes
    return () => {
      const stale = document.getElementById(ldId);
      if (stale) stale.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: BRAND.cream }} data-testid="book-page">
      {/* HERO */}
      <section
        className="px-6 pt-16 pb-20 md:pt-24 md:pb-28"
        style={{
          background: `linear-gradient(160deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`,
          color: BRAND.cream,
        }}
        data-testid="book-hero"
      >
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Cover */}
          <div className="order-2 md:order-1 flex justify-center md:justify-end">
            <img
              src="/book-cover-2026.png"
              alt="Stop saying 'I Have To.' Start living 'I GET TO.' — book cover"
              className="w-full max-w-xs md:max-w-sm shadow-2xl rounded-sm"
              style={{ boxShadow: `0 30px 80px rgba(0,0,0,0.45), 0 0 0 1px ${BRAND.gold}33` }}
              data-testid="book-cover-image"
            />
          </div>

          {/* Copy */}
          <div className="order-1 md:order-2">
            <p
              className="font-sans text-xs tracking-[0.4em] uppercase mb-5"
              style={{ color: BRAND.gold }}
              data-testid="book-eyebrow"
            >
              {BOOK_STATUS === "LIVE"
                ? "The Book · Now Shipping"
                : "The Book · Premium Edition Coming Soon"}
            </p>
            <h1
              className="font-serif tracking-tight leading-[0.95] mb-7"
              style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.2rem)" }}
              data-testid="book-title"
            >
              Stop saying <span className="italic">"I Have To."</span>
              <br />
              Start living{" "}
              <span style={{ color: BRAND.mint }} className="italic">
                "I GET TO."
              </span>
            </h1>
            <p
              className="font-serif italic leading-relaxed mb-6"
              style={{ fontSize: "clamp(1.1rem, 1.6vw, 1.4rem)", color: `${BRAND.cream}dd` }}
            >
              The Mind Body Spirit HYGIENE™ Method — real tools, for real women, in the messy
              middle.
            </p>

            <div
              className="my-7 h-px"
              style={{ background: `linear-gradient(90deg, ${BRAND.gold}aa, transparent)` }}
            />

            <p className="font-sans text-base leading-relaxed mb-8 max-w-xl" style={{ opacity: 0.9 }}>
              You woke up and didn't quite recognize the woman in the mirror. Maybe you're
              divorced — or wish you were. Maybe a kid won't call you back. Maybe your bloodwork
              keeps coming back "normal" while your body screams something else. Maybe you said
              yes when you meant no — again — and you're sitting in the cost of it.
            </p>
            <p
              className="font-serif italic text-xl mb-10"
              style={{ color: BRAND.mint }}
              data-testid="book-tagline"
            >
              This book is for her.
            </p>

            {/* CTA — scrolls to the two-edition chooser (nothing featured) */}
            <div className="flex flex-col gap-6">
              <ChooseEditionCta />
              <div className="flex flex-wrap gap-4 items-center pt-2 border-t" style={{ borderColor: `${BRAND.cream}22` }}>
                <div className="pt-4 w-full md:w-auto">
                  <BuyOrNotify variant="hero" />
                </div>
                <a
                  href="#sponsor"
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-full font-sans tracking-wide border transition-colors"
                  style={{
                    borderColor: `${BRAND.gold}aa`,
                    color: BRAND.gold,
                    fontSize: "14px",
                  }}
                  data-testid="sponsor-anchor-cta"
                >
                  ✦ Sponsor 100 copies to a women's shelter
                </a>
              </div>
            </div>

            <p
              className="font-sans text-xs mt-6 tracking-wide"
              style={{ color: `${BRAND.cream}99` }}
              data-testid="book-availability-note"
            >
              {BOOK_STATUS === "LIVE"
                ? "Now available in paperback on Amazon · plus an instant PDF edition on this page"
                : "Premium edition coming soon · larger trim size · interior redesigned · join the list for launch"}
            </p>
          </div>
        </div>
      </section>

      {/* TWO EDITIONS — one method. The primary purchase decision. */}
      <EditionChooser />

      {/* FIRST READER TESTIMONIAL — moved higher (below hero) for visibility */}
      <section
        className="px-6 py-16 md:py-20"
        style={{
          background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`,
          color: BRAND.cream,
        }}
        data-testid="reader-testimonials"
      >
        <div className="max-w-4xl mx-auto text-center">
          <p
            className="font-sans text-xs tracking-[0.4em] uppercase mb-5"
            style={{ color: BRAND.gold }}
            data-testid="testimonials-eyebrow"
          >
            ✦ First Reader · Verified ✦
          </p>
          <h2
            className="font-serif tracking-tight mb-12"
            style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", color: BRAND.cream }}
            data-testid="testimonials-headline"
          >
            What early readers are saying
          </h2>

          <figure data-testid="testimonial-cb-washington" className="max-w-3xl mx-auto">
            <div
              className="font-serif italic leading-relaxed mb-8"
              style={{
                fontSize: "clamp(1.15rem, 2.2vw, 1.55rem)",
                color: BRAND.cream,
              }}
            >
              <span style={{ color: BRAND.gold, fontSize: "2rem", lineHeight: "0" }}>“</span>
              I just finished your book. <strong style={{ color: BRAND.gold, fontWeight: 600 }}>BLEW ME AWAY!</strong> I am so in awe of you. I loved every part of it — it took me some time, but I loved that, because I was able to take in each chapter. I loved the acronyms and breakdown of words, and building new neural pathways to what those words mean for you. I hope the launch of your book was a MASSIVE success, because not only is this a book, but a tool that needs to be studied and applied in many people&apos;s lives.
              <span style={{ color: BRAND.gold, fontSize: "2rem", lineHeight: "0" }}>”</span>
            </div>
            <figcaption
              className="font-sans text-sm tracking-[0.2em] uppercase"
              style={{ color: BRAND.mint }}
            >
              — CB · Washington State
            </figcaption>
          </figure>
        </div>
      </section>

      {/* FREE WORKBOOK — inline email capture (scrolled to from the mint CTA, no page hop) */}
      <section
        id="workbook-capture"
        className="px-6 py-16 md:py-20 scroll-mt-20"
        style={{ background: BRAND.teal, color: BRAND.cream }}
        data-testid="workbook-capture-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-sans text-xs tracking-[0.4em] uppercase mb-4" style={{ color: BRAND.cream }}>
            Free · Instant download
          </p>
          <h2
            className="font-serif tracking-tight leading-[1.05] mb-4"
            style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)" }}
            data-testid="workbook-capture-headline"
          >
            Get the free <span className="italic" style={{ color: BRAND.deep }}>I GET TO</span> workbook
          </h2>
          <p className="font-serif italic text-lg leading-relaxed mb-8 max-w-xl mx-auto" style={{ color: `${BRAND.cream}dd` }}>
            The printable workbook — real-tool reframes for the messy middle. Drop your email and it's yours in seconds.
          </p>
          <WorkbookInlineForm />
          <p className="font-sans text-xs mt-5 tracking-wide" style={{ color: `${BRAND.cream}aa` }}>
            One email, no spam, unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* FREE 8-PAGE SAMPLE — lead magnet email capture */}
      <section
        id="free-chapter"
        className="px-6 py-16 md:py-20 scroll-mt-20"
        style={{ background: BRAND.deep, color: BRAND.cream }}
        data-testid="sample-section"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p
              className="font-sans text-xs tracking-[0.4em] uppercase mb-5"
              style={{ color: BRAND.gold }}
            >
              Read it before you buy it
            </p>
            <h2
              className="font-serif tracking-tight leading-[1.05] mb-6"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.4rem)" }}
              data-testid="sample-headline"
            >
              Read the first <span style={{ color: BRAND.mint, fontStyle: "italic" }}>8 pages</span>
              <br />
              free.
            </h2>
            <p
              className="font-serif italic text-lg md:text-xl leading-relaxed mb-3 max-w-2xl mx-auto"
              style={{ color: `${BRAND.cream}cc` }}
            >
              See if this book lands before you spend a dollar.
            </p>
            <p
              className="font-sans text-sm md:text-base leading-relaxed max-w-xl mx-auto"
              style={{ color: BRAND.mint }}
              data-testid="sample-bundle-note"
            >
              ✦ Plus — my free <strong>12-Month I GET TO Calendar</strong>, on the house.
            </p>
          </div>

          {/* Enticement: book cover + "what you get" bullets */}
          <div
            className="grid md:grid-cols-[200px_1fr] gap-8 md:gap-10 items-center mb-10 max-w-3xl mx-auto text-left"
            data-testid="sample-enticement"
          >
            <img
              src="/book-cover-2026.png"
              alt="Stop saying 'I Have To.' Start living 'I GET TO.' — book cover"
              loading="lazy"
              className="w-40 md:w-full mx-auto rounded-md"
              style={{
                boxShadow: `0 18px 40px ${BRAND.deep}88, 0 0 0 1px ${BRAND.mint}33`,
              }}
              data-testid="sample-cover-image"
            />
            <ul className="space-y-4 font-serif text-base md:text-lg leading-relaxed">
              <li className="flex gap-3 items-start">
                <span style={{ color: BRAND.mint, fontSize: "20px", lineHeight: 1 }}>✦</span>
                <span style={{ color: `${BRAND.cream}ee` }}>
                  <strong style={{ color: BRAND.mint }}>The full opening</strong> — front cover, dedication, acknowledgment, and the I GET TO teaching that started everything.
                </span>
              </li>
              <li className="flex gap-3 items-start">
                <span style={{ color: BRAND.mint, fontSize: "20px", lineHeight: 1 }}>✦</span>
                <span style={{ color: `${BRAND.cream}ee` }}>
                  <strong style={{ color: BRAND.mint }}>The 12-Month I GET TO Calendar</strong> — one printable page per month, a simple HYGIENE practice to anchor your year.
                </span>
              </li>
              <li className="flex gap-3 items-start">
                <span style={{ color: BRAND.mint, fontSize: "20px", lineHeight: 1 }}>✦</span>
                <span style={{ color: `${BRAND.cream}ee` }}>
                  <strong style={{ color: BRAND.mint }}>Weekly walk-with-me emails</strong> — real tools for the messy middle, never spam, unsubscribe anytime.
                </span>
              </li>
            </ul>
          </div>

          <div className="flex justify-center">
            <SampleDownloadForm />
          </div>

          <p
            className="font-sans text-xs mt-5 tracking-wide text-center"
            style={{ color: `${BRAND.cream}88` }}
          >
            One email, no spam, unsubscribe anytime. Your sample arrives instantly.
          </p>
        </div>
      </section>

      {/* PRICE TRANSPARENCY NOTE — in Temple's voice */}
      <section
        className="px-6 py-14 md:py-16"
        style={{ background: BRAND.cream }}
        data-testid="course-upsell-section"
      >
        <div className="max-w-3xl mx-auto">
          <CourseBanner product="bundle" variant="card" testId="book-course-upsell" />
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="px-6 py-20 md:py-28" data-testid="inside-section">
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-xs tracking-[0.4em] uppercase mb-4"
            style={{ color: BRAND.teal }}
          >
            Inside the book
          </p>
          <h2
            className="font-serif tracking-tight mb-10"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)", color: BRAND.deep, lineHeight: 1.05 }}
          >
            This is BIG BRAVE work.
            <br />
            <span className="italic" style={{ color: BRAND.teal }}>
              Daily transformation. Non-negotiable. Yours.
            </span>
          </h2>

          <ul className="space-y-5 font-serif text-lg leading-relaxed" style={{ color: BRAND.deep }}>
            {[
              "The A.C.T. NOW Method™ — Awareness · Clarity · Tapped In · No Opportunity Wasted. The framework for rebuilding from the studs.",
              "Daily tools for mind, body, and spirit hygiene — non-negotiable, doable, yours.",
              "The PCC — your Personal Code of Conduct. Non-negotiables for yourself, by yourself.",
              "A real, grounded practice for tapping into the energies of your day with free will and discernment.",
              "Honest language for grief, estrangement, midlife reckoning, and the messy middle nobody warned you about.",
            ].map((item, idx) => (
              <li key={idx} className="flex gap-4" data-testid={`inside-item-${idx}`}>
                <span style={{ color: BRAND.gold, fontSize: "1.4rem", flexShrink: 0 }}>✦</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div
            className="mt-12 p-7 rounded-2xl font-serif italic text-lg leading-relaxed"
            style={{
              background: `${BRAND.teal}11`,
              borderLeft: `3px solid ${BRAND.gold}`,
              color: BRAND.deep,
            }}
            data-testid="welcome-home-pullquote"
          >
            Written for the woman in the messy middle — divorced, estranged, recovering, rebuilding,
            tired of performing wellness — this book offers real tools from a real woman who's been
            there. No toxic positivity. No spiritual bypass. No identity gatekeeping.
            <br />
            <br />
            <span style={{ color: BRAND.teal, fontStyle: "normal", fontWeight: 500 }}>
              Real tools, for real women, in the messy middle. Welcome home.
            </span>
          </div>
        </div>
      </section>

      {/* SPONSOR 100 — DONATION TIER */}
      <section
        id="sponsor"
        className="px-6 py-20 md:py-28"
        style={{ background: BRAND.deep, color: BRAND.cream }}
        data-testid="sponsor-section"
      >
        <div className="max-w-4xl mx-auto">
          <p
            className="font-sans text-xs tracking-[0.4em] uppercase mb-5"
            style={{ color: BRAND.gold }}
          >
            Sponsor a Women's Shelter
          </p>
          <h2
            className="font-serif tracking-tight leading-[1.05] mb-7"
            style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.6rem)" }}
            data-testid="sponsor-headline"
          >
            Buy 100. Send them somewhere
            <br />
            <span className="italic" style={{ color: BRAND.mint }}>
              she will actually be held.
            </span>
          </h2>

          <p
            className="font-serif italic text-xl leading-relaxed mb-10 max-w-2xl"
            style={{ color: `${BRAND.cream}dd` }}
            data-testid="sponsor-lede"
          >
            Every woman who walks into a domestic violence shelter is, by definition, in the messy
            middle. She's leaving. She's grieving. She's rebuilding from the studs with one bag and
            a brave breath. This book was written for her too.
          </p>

          <div
            className="grid md:grid-cols-3 gap-6 mb-12"
            data-testid="sponsor-tiers"
          >
            {[
              {
                label: "Hold One Woman",
                qty: "1 copy",
                price: "$19.99",
                note: "Donate one copy to a woman who needs to hear: this book is for her.",
                testid: "tier-1",
              },
              {
                label: "Hold a Circle",
                qty: "10 copies",
                price: "$199",
                note: "A support-group's worth. One shelter Circle. Ten women, one book each.",
                testid: "tier-10",
                featured: false,
              },
              {
                label: "Sponsor a Women's Shelter",
                qty: "100 copies",
                price: "$1,999",
                note: "Stock an entire women's shelter's library + intake bag program. We'll handle shipping direct to the shelter of your choice.",
                testid: "tier-100",
                featured: true,
              },
            ].map((tier) => (
              <div
                key={tier.label}
                className="p-7 rounded-2xl flex flex-col"
                style={{
                  background: tier.featured ? `${BRAND.mint}11` : `${BRAND.cream}0d`,
                  border: `1px solid ${tier.featured ? BRAND.mint : `${BRAND.cream}22`}`,
                  position: "relative",
                }}
                data-testid={tier.testid}
              >
                {tier.featured && (
                  <span
                    className="font-sans text-[10px] tracking-[0.3em] uppercase font-semibold mb-3"
                    style={{ color: BRAND.mint }}
                  >
                    Most Impact
                  </span>
                )}
                <h3
                  className="font-serif text-2xl mb-2"
                  style={{ color: tier.featured ? BRAND.mint : BRAND.cream }}
                >
                  {tier.label}
                </h3>
                <p
                  className="font-sans text-xs tracking-widest uppercase mb-4"
                  style={{ color: BRAND.gold }}
                >
                  {tier.qty}
                </p>
                <p
                  className="font-serif text-3xl mb-4"
                  style={{ color: BRAND.cream }}
                >
                  {tier.price}
                </p>
                <p
                  className="font-sans text-sm leading-relaxed mb-6"
                  style={{ color: `${BRAND.cream}cc` }}
                >
                  {tier.note}
                </p>
                <a
                  href={`mailto:${SPONSOR_EMAIL}?subject=Sponsor%20${encodeURIComponent(
                    tier.label
                  )}%20—%20${encodeURIComponent(tier.qty)}&body=Hi%20Temple%2C%0A%0AI%27d%20like%20to%20sponsor%20the%20%22${encodeURIComponent(
                    tier.label
                  )}%22%20tier%20(${encodeURIComponent(tier.qty)}).%0A%0AMy%20preferred%20shelter%20to%20receive%20the%20books%3A%0A%0AHow%20to%20reach%20me%3A%0A%0AThank%20you.`}
                  className="mt-auto inline-block text-center px-6 py-3 rounded-full font-sans font-semibold transition-all hover:scale-[1.02]"
                  style={{
                    background: tier.featured ? BRAND.mint : "transparent",
                    color: tier.featured ? BRAND.deep : BRAND.mint,
                    border: tier.featured ? "none" : `1px solid ${BRAND.mint}`,
                    fontSize: "13px",
                    letterSpacing: "0.05em",
                  }}
                  data-testid={`${tier.testid}-cta`}
                >
                  {tier.featured ? "Sponsor 100 books to a women's shelter →" : "Start the conversation →"}
                </a>
              </div>
            ))}
          </div>

          <div
            className="p-7 rounded-2xl font-serif italic text-lg leading-relaxed"
            style={{
              background: `${BRAND.gold}11`,
              borderLeft: `3px solid ${BRAND.gold}`,
              color: `${BRAND.cream}ee`,
            }}
            data-testid="sponsor-howitworks"
          >
            <p className="font-sans text-xs tracking-[0.3em] uppercase font-semibold mb-3" style={{color: BRAND.gold}}>
              How it works
            </p>
            <p className="text-base font-sans not-italic leading-relaxed">
              Email me at{" "}
              <a
                href={`mailto:${SPONSOR_EMAIL}`}
                style={{ color: BRAND.mint, textDecoration: "underline" }}
              >
                {SPONSOR_EMAIL}
              </a>
              . Tell me how many copies and — if you have one in mind — the shelter you'd like to
              receive them. If you don't, I'll match your books with a vetted women's shelter that
              serves women in midlife reckoning, divorce, estrangement, or recovery. You pay; Lulu
              prints; the books ship direct to the shelter with a handwritten note from us both. You
              receive a thank-you and a tax receipt where applicable.
            </p>
          </div>
        </div>
      </section>

      {/* ABOUT THE AUTHOR */}
      <section className="px-6 py-20 md:py-24" data-testid="author-section">
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-xs tracking-[0.4em] uppercase mb-5"
            style={{ color: BRAND.teal }}
          >
            About the Author
          </p>
          <h2
            className="font-serif tracking-tight mb-7 leading-[1.05]"
            style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", color: BRAND.deep }}
          >
            Temple Franklin built this method because{" "}
            <span className="italic" style={{ color: BRAND.teal }}>
              no elder was in the room when she needed one.
            </span>
          </h2>
          <p
            className="font-serif text-lg leading-relaxed mb-5"
            style={{ color: BRAND.deep }}
          >
            Adopted at birth. Married twenty-two years to a life that was eroding emotionally for
            years. Mother to three daughters and the slow grief of estrangement. Brought down to
            the studs by undeniable grief, Hashimoto's, and a breast cancer scare.
          </p>
          <p
            className="font-serif text-lg leading-relaxed mb-5"
            style={{ color: BRAND.deep }}
          >
            She trained for three years with a renowned meditation teacher. She sat with mediums.
            She researched Mind, Body, and Spirit for over twenty years — because no one had given
            her the language. And then, one ordinary morning at the kitchen trash can, three words
            landed in the middle of her chest: <em>I GET TO</em>.
          </p>
          <p
            className="font-serif text-lg leading-relaxed"
            style={{ color: BRAND.deep }}
          >
            That moment cracked open everything that became this work. So she became the elder she
            needed. This is the inheritance she had to build from scratch — and the one she's
            handing to you.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="px-6 py-20 md:py-24"
        style={{ background: `${BRAND.teal}0a` }}
        data-testid="faq-section"
      >
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-xs tracking-[0.4em] uppercase mb-5"
            style={{ color: BRAND.teal }}
          >
            Honest answers
          </p>
          <h2
            className="font-serif tracking-tight mb-10"
            style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", color: BRAND.deep }}
          >
            Before you order
          </h2>

          <div className="space-y-7" data-testid="faq-list">
            {BOOK_FAQ.map((item, idx) => (
              <div key={idx} data-testid={`faq-item-${idx}`}>
                <h3
                  className="font-serif text-xl mb-2"
                  style={{ color: BRAND.deep }}
                >
                  {item.q}
                </h3>
                <p
                  className="font-sans text-base leading-relaxed"
                  style={{ color: `${BRAND.deep}cc` }}
                >
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section
        className="px-6 py-20 text-center"
        style={{
          background: `linear-gradient(180deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`,
          color: BRAND.cream,
        }}
        data-testid="footer-cta"
      >
        <p
          className="font-serif italic text-2xl md:text-3xl mb-8 max-w-2xl mx-auto leading-relaxed"
          style={{ color: BRAND.cream }}
        >
          Real tools, for real women, in the messy middle.
          <br />
          <span style={{ color: BRAND.mint }}>Welcome home.</span>
        </p>
        <div className="flex flex-col items-center gap-6">
          <ChooseEditionCta block />
          <div className="pt-4 border-t w-full flex justify-center" style={{ borderColor: `${BRAND.cream}22` }}>
            <div className="pt-6">
              <BuyOrNotify variant="footer" />
            </div>
          </div>
        </div>
        <p
          className="font-sans text-xs mt-6 tracking-wide"
          style={{ color: `${BRAND.cream}88` }}
        >
          ISBN: 979-8-9964172-1-6 · Published by Tapped In Publishing · Paperback · 6 × 9
        </p>
      </section>
    </div>
  );
};

export default BookPage;
